import React, { useEffect, useRef, useState } from "react";

interface EmailBodyEditorProps {
  value: string;
  onChange: (nextValue: string) => void;
  label?: string;
  helperText?: string;
  minRows?: number;
}

type EditorMode = "visual" | "html";

const TOOLBAR_ACTIONS: Array<
  | { id: "bold" | "italic" | "underline"; label: string; icon: string }
  | { id: "ordered-list" | "unordered-list"; label: string; icon: string }
  | { id: "link"; label: string; icon: string }
  | { id: "remove-format"; label: string; icon: string }
> = [
  { id: "bold", label: "Bold", icon: "B" },
  { id: "italic", label: "Italic", icon: "I" },
  { id: "underline", label: "Underline", icon: "U" },
  { id: "unordered-list", label: "Bullet list", icon: "•" },
  { id: "ordered-list", label: "Numbered list", icon: "1." },
  { id: "link", label: "Add link", icon: "🔗" },
  { id: "remove-format", label: "Clear", icon: "⌫" },
];

export const EmailBodyEditor: React.FC<EmailBodyEditorProps> = ({
  value,
  onChange,
  label,
  helperText,
  minRows = 8,
}) => {
  const [mode, setMode] = useState<EditorMode>("visual");
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mode === "visual" && editorRef.current) {
      const current = editorRef.current.innerHTML;
      if (current !== (value || "")) {
        editorRef.current.innerHTML = value || "";
      }
    }
  }, [mode, value]);

  const handleVisualInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const toggleMode = () => {
    setMode((current) => (current === "visual" ? "html" : "visual"));
  };

  const execCommand = (actionId: typeof TOOLBAR_ACTIONS[number]["id"]) => {
    if (mode !== "visual" || !editorRef.current) return;

    editorRef.current.focus();
    const commandMap: Record<typeof actionId, string> = {
      bold: "bold",
      italic: "italic",
      underline: "underline",
      "unordered-list": "insertUnorderedList",
      "ordered-list": "insertOrderedList",
      link: "createLink",
      "remove-format": "removeFormat",
    };

    if (actionId === "link") {
      const url = window.prompt("Enter URL");
      if (!url) return;
      document.execCommand(commandMap[actionId], false, url);
    } else {
      document.execCommand(commandMap[actionId], false);
    }

    handleVisualInput();
  };

  const handleToolbarClick = (
    actionId: typeof TOOLBAR_ACTIONS[number]["id"],
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    execCommand(actionId);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {label ? (
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {label}
          </span>
        ) : null}
        <button
          type="button"
          onClick={toggleMode}
          className="text-xs font-semibold text-[var(--primary-color)] hover:text-[var(--primary-color-dark)]"
        >
          {mode === "visual" ? "Edit HTML" : "Use visual editor"}
        </button>
      </div>

      {mode === "visual" ? (
        <>
          <div className="flex flex-wrap items-center gap-1 rounded-xl border border-gray-200 bg-white px-2 py-1 shadow-sm">
            {TOOLBAR_ACTIONS.map((action) => (
              <button
                key={action.id}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => handleToolbarClick(action.id, event)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
                title={action.label}
              >
                {action.icon}
              </button>
            ))}
          </div>
          <div
            ref={editorRef}
            contentEditable
            onInput={handleVisualInput}
            style={{ minHeight: `${Math.max(minRows, 4) * 20}px` }}
            className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
            data-placeholder="Start typing your email…"
            suppressContentEditableWarning
          />
        </>
      ) : (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={minRows}
          className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-mono leading-relaxed focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
        />
      )}

      {helperText ? (
        <span className="text-xs text-gray-500">{helperText}</span>
      ) : null}
    </div>
  );
};

export default EmailBodyEditor;
